import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ContactForm = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    companySize: '',
    industry: '',
    primaryChallenge: '',
    phone: ''
  });

  const [errors, setErrors] = useState({});

  const companySizeOptions = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-1000', label: '201-1,000 employees' },
    { value: '1000+', label: '1,000+ employees' }
  ];

  const industryOptions = [
    { value: 'technology', label: 'Technology' },
    { value: 'financial-services', label: 'Financial Services' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'education', label: 'Education' },
    { value: 'government', label: 'Government' },
    { value: 'other', label: 'Other' }
  ];

  const challengeOptions = [
    { value: 'data-quality', label: 'Data Quality Issues' },
    { value: 'analytics-adoption', label: 'Analytics Adoption' },
    { value: 'governance-compliance', label: 'Governance & Compliance' },
    { value: 'team-skills', label: 'Team Skills Gap' },
    { value: 'technology-integration', label: 'Technology Integration' },
    { value: 'strategic-alignment', label: 'Strategic Alignment' },
    { value: 'roi-measurement', label: 'ROI Measurement' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData?.company?.trim()) newErrors.company = 'Company name is required';
    if (!formData?.jobTitle?.trim()) newErrors.jobTitle = 'Job title is required';
    if (!formData?.companySize) newErrors.companySize = 'Company size is required';
    if (!formData?.industry) newErrors.industry = 'Industry is required';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-subtle p-6 hover:shadow-elevation transition-shadow duration-300">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-3 hover:bg-secondary/20 hover:scale-110 transition-all duration-300">
          <Icon name="User" size={24} className="text-secondary" />
        </div>
        <h3 className="text-xl font-semibold text-primary mb-2">Get Your Personalized Report</h3>
        <p className="text-muted-foreground text-sm">
          Complete your profile to receive detailed insights and recommendations
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="First Name"
            type="text"
            placeholder="Enter your first name"
            value={formData?.firstName}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            error={errors?.firstName}
            required
          />
          <Input
            label="Last Name"
            type="text"
            placeholder="Enter your last name"
            value={formData?.lastName}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            error={errors?.lastName}
            required
          />
        </div>

        <Input
          label="Email Address"
          type="email"
          placeholder="Enter your business email"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          required
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Company Name"
            type="text"
            placeholder="Enter your company name"
            value={formData?.company}
            onChange={(e) => handleInputChange('company', e?.target?.value)}
            error={errors?.company}
            required
          />
          <Input
            label="Job Title"
            type="text"
            placeholder="Enter your job title"
            value={formData?.jobTitle}
            onChange={(e) => handleInputChange('jobTitle', e?.target?.value)}
            error={errors?.jobTitle}
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Company Size"
            placeholder="Select company size"
            options={companySizeOptions}
            value={formData?.companySize}
            onChange={(value) => handleInputChange('companySize', value)}
            error={errors?.companySize}
            required
          />
          <Select
            label="Industry"
            placeholder="Select your industry"
            options={industryOptions}
            value={formData?.industry}
            onChange={(value) => handleInputChange('industry', value)}
            error={errors?.industry}
            required
          />
        </div>

        <Select
          label="Primary Data Challenge"
          placeholder="What's your biggest data challenge?"
          description="This helps us provide more targeted recommendations"
          options={challengeOptions}
          value={formData?.primaryChallenge}
          onChange={(value) => handleInputChange('primaryChallenge', value)}
        />

        <Input
          label="Phone Number (Optional)"
          type="tel"
          placeholder="Enter your phone number"
          description="For priority consultation scheduling"
          value={formData?.phone}
          onChange={(e) => handleInputChange('phone', e?.target?.value)}
        />

        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={isLoading}
          iconName="ArrowRight"
          iconPosition="right"
          className="bg-secondary hover:bg-secondary/90 hover:scale-105 hover:shadow-elevation focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95 transition-all duration-200 mt-6"
        >
          Generate My Report
        </Button>
      </form>
      <div className="mt-4 text-center">
        <p className="text-xs text-muted-foreground">
          By submitting, you agree to receive insights and updates from Northstar Analytics. 
          We respect your privacy and never share your information.
        </p>
      </div>
    </div>
  );
};

export default ContactForm;